import { PlusInSquareIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { useBoolean } from "hooks";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, RadioButton } from "ui";

import { ProductBothTogetherMethod } from "../../../../constants/enums";
import { ProductsSelector, ProductsSelectorTable } from "../../../../widgets";
import { ProductFormValues } from "../ProductDetailsForm.types";

import styles from "../ProductDetailsForm.module.scss";

export const BothTogetherMethodBlock = () => {
  const { t } = useTranslation();
  const {
    value: isSelectProductsModalOpen,
    setTrue: openSelectProductsModal,
    setFalse: closeSelectProductsModal
  } = useBoolean(false);

  const { control, register, watch } = useFormContext<ProductFormValues>();

  const { update: updateProductVariant } = useFieldArray({
    control,
    name: "variants"
  });

  const productBothTogetherMethodWatch = watch("productBothTogetherMethod");

  const productVariants = watch("variants");

  return (
    <div className={styles.productForm__block}>
      <h4 className={styles.productForm__block__title}>
        {t("Products frequently bought together")}
      </h4>
      <div className={styles.productForm__radios}>
        <RadioButton
          label={t("No products")}
          {...register("productBothTogetherMethod")}
          value={ProductBothTogetherMethod.NO_PRODUCTS}
        />
        <RadioButton
          label={t("One set for all variants")}
          {...register("productBothTogetherMethod")}
          value={ProductBothTogetherMethod.ONE_SET_FOR_ALL_VARIANTS}
        />
        <RadioButton
          label={t("Products for each variant")}
          {...register("productBothTogetherMethod")}
          value={ProductBothTogetherMethod.PRODUCTS_FOR_EACH_VARIANT}
        />
      </div>

      <If
        condition={
          productBothTogetherMethodWatch ===
          ProductBothTogetherMethod.PRODUCTS_FOR_EACH_VARIANT
        }
      >
        <>
          <p className={styles.productForm__describeText}>
            {t("Add up to two products separately for each variant.")}
          </p>

          <p
            className={cn(
              styles.productForm__describeText,
              styles.productForm__describeText__withBackground
            )}
          >
            {t(
              "You’ll be able to add products within the variant customization."
            )}
          </p>
        </>
      </If>

      <If
        condition={
          productBothTogetherMethodWatch ===
          ProductBothTogetherMethod.ONE_SET_FOR_ALL_VARIANTS
        }
      >
        <>
          {productVariants[0]?.variantsBoughtTogether[0] && (
            <ProductsSelectorTable
              products={productVariants[0]?.variantsBoughtTogether ?? []}
              onChange={(value) => {
                productVariants.forEach((variant, index) => {
                  updateProductVariant(index, {
                    ...variant,
                    variantsBoughtTogether: value
                  });
                });
              }}
              value={productVariants[0]?.variantsBoughtTogether ?? []}
              isLoading={false}
            />
          )}
          <ProductsSelector
            isOpen={isSelectProductsModalOpen}
            onClose={closeSelectProductsModal}
            value={productVariants[0]?.variantsBoughtTogether ?? []}
            onChange={(value) => {
              productVariants.forEach((variant, index) => {
                updateProductVariant(index, {
                  ...variant,
                  variantsBoughtTogether: value
                });
              });
            }}
            maxValueLength={2}
            excludeVariantIds={
              productVariants
                .map((variant) => variant.id)
                .filter(Boolean) as string[]
            }
          />

          {(!productVariants[0] ||
            productVariants[0]?.variantsBoughtTogether.length) < 2 && (
            <Button
              type="button"
              icon={<PlusInSquareIcon />}
              variant="default"
              disabled={!productVariants[0]}
              onClick={openSelectProductsModal}
            >
              {t("Add products")}
            </Button>
          )}

          {!productVariants[0] && (
            <div className={styles.productForm__describeText}>
              {t("First, add at least one color variant")}
            </div>
          )}
        </>
      </If>
    </div>
  );
};
